import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ColumnApi } from 'ag-grid-community';
import { Store } from 'components/common/types/Store.types';
import { MetricType } from 'components/common/types/DrilldownReports.types';
import { getLocaleCode, getMarketId } from 'store/publisher/selectors';

export interface CommonReportState {
  isLoading: boolean;
  selectedMetricType: MetricType;
  startOfLoadingTimeInMs?: number;
  columnApi: ColumnApi | null;
}

export interface CommonReportSelectors {
  marketId: string;
  localeCode: string;
  campaignType: string;
}

export interface UseReportStateReturn {
  isLoading: boolean;
  startOfLoadingTimeInMs?: number;
  columnApi: ColumnApi | null;
  marketId: string;
  localeCode: string;
  campaignType: string;
  setIsLoading: (loading: boolean) => void;
  setStartOfLoadingTimeInMs: (time: number | undefined) => void;
  setColumnApi: (api: ColumnApi | null) => void;
  selectedMetricType: MetricType;
  handleChangeMetricType: (metricType: MetricType) => void;
}

export const useReportState = (type: 'allocation' | 'availability'): UseReportStateReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [startOfLoadingTimeInMs, setStartOfLoadingTimeInMs] = useState<number>();
  const [columnApi, setColumnApi] = useState<ColumnApi | null>(null);

  const campaignPlanReportMetricType = useSelector(
    (state: Store) => state.sharedCampaignPlan.campaignPlanDetails?.metaData?.metricType,
  );
  const reportMetricType = useSelector((state: Store) => state.publisher.reportConfiguration.metricType);

  const [selectedMetricType, setSelectedMetricType] = useState<MetricType>(() => {
    if (type === 'allocation') {
      return campaignPlanReportMetricType ?? reportMetricType ?? MetricType.FRAMES;
    }

    return reportMetricType ?? MetricType.FRAMES;
  });

  const marketId = useSelector(getMarketId);
  const localeCode = useSelector(getLocaleCode);
  const campaignType = useSelector((state: Store) => state.dealManagement.campaignType);

  return {
    isLoading,
    startOfLoadingTimeInMs,
    columnApi,
    marketId,
    localeCode,
    campaignType,
    setIsLoading,
    setStartOfLoadingTimeInMs,
    setColumnApi,
    selectedMetricType,
    handleChangeMetricType: (metricType: MetricType) => setSelectedMetricType(metricType),
  };
};
